import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import { AppVersion } from '@ionic-native/app-version/ngx';
import { FingerprintAIO, FingerprintOptions } from '@ionic-native/fingerprint-aio/ngx';
import { InAppBrowser, InAppBrowserObject } from '@ionic-native/in-app-browser/ngx';
import { AlertController, Platform, PopoverController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import { NoticeAfterLoginModalComponent } from '@shared/components/modals/notice-after-login-modal/notice-after-login-modal.component';
import { ConnectionStatus } from '@shared/models/constants/ConnectionStatus.enum';
import { PROFILEIDS } from '@shared/models/constants/profileIds';
import { Plan } from '@shared/models/interfaces/plan.interface';
import { AuthenticationService } from '@shared/providers/authentication.service';
import { IndexedDBService } from '@shared/providers/indexed-db.service';
import { LeadService } from '@shared/providers/lead.service';
import { NetworkService } from '@shared/providers/network.service';
import { PerformSurveyService } from '@shared/providers/perform-survey.service';
import { TokenService } from '@shared/providers/token.service';
import { UtilsService } from '@shared/providers/utils.service';
import { UserInformations, UserTokenData } from '@shared/store/logged-user/user.interface';
import { SetNotificationsOpened } from '@shared/store/notification/notification.actions';
import { NotificationState } from '@shared/store/notification/notification.state';
import { Observable } from 'rxjs';
import { LoginService } from '../login.service';

const KEY_ARRAY_ID_SHOWN = 'arrayIdShown';
@Component({
  selector: 'app-login-form',
  templateUrl: './login-form.page.html',
  styleUrls: ['./login-form.page.scss'],
})
export class LoginFormPage implements OnInit {
  @Select(NotificationState.getNotificationOpened) notificationOpened$: Observable<boolean>;
  public loginForm: FormGroup;
  public tokenWhenUserHasExperiredPlan: string;
  public showPass = false;
  feedbackMessage = {
    401: `${this.translate.instant('LOGIN.NOT_AUTHORIZED')}`,
  };
  public lastUserLogged: any;
  public isMobile = false;
  public appVersionNumber: string;
  public EAD_SURVEY_URL = environment.eadMakeSurvey;

  showNoteWithoutCredentials: boolean = false;
  showNoteWithCredentials: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private utils: UtilsService,
    private loginService: LoginService,
    private tokenService: TokenService,
    private faio: FingerprintAIO,
    private platform: Platform,
    private store: Store,
    private networkService: NetworkService,
    private appVersion: AppVersion,
    private performSurveyService: PerformSurveyService,
    private alertController: AlertController,
    private leadService: LeadService,
    private translate: TranslateService,
    private popoverController: PopoverController,
    private iab: InAppBrowser,
    private indexedDBService: IndexedDBService
  ) {}

  ngOnInit() {
    this.loginForm = this.initializeForm();
    this.platform
      .ready()
      .then(async () => {
        this.isMobile = this.platform.is('mobile');
        this.appVersionNumber = await this.appVersion.getVersionNumber();
      })
      .catch(() => {});
  }

  ionViewDidEnter() {
    this.lastUserLogged = JSON.parse(localStorage.getItem('lastUserLogged'));
    if (this.lastUserLogged && this.isMobile) {
      this.username.setValue(this.lastUserLogged);
      this.login(this.loginForm);
    }
  }

  accessTraining(trainingOption: string): void {
    let browser: InAppBrowserObject;

    switch (trainingOption) {
      case 'makeSurvey':
        browser = this.iab.create(this.EAD_SURVEY_URL, '_system');
        break;
    }

    browser.show();
  }

  initializeForm() {
    return this.formBuilder.group({
      username: ['', Validators.compose([Validators.required, Validators.email, Validators.maxLength(90)])],
      password: [
        '',
        Validators.compose([Validators.required, Validators.minLength(8), Validators.maxLength(180)]),
      ],
      keepSession: [true],
    });
  }

  get username() {
    return this.loginForm.get('username');
  }

  get password() {
    return this.loginForm.get('password');
  }

  get keepSession() {
    return this.loginForm.get('keepSession');
  }

  async login(form: FormGroup) {
    const { username, password, keepSession } = form.getRawValue();

    const userStored = JSON.parse(localStorage.getItem(username));
    if (userStored) {
      if (userStored.useFingerPrint && !userStored.canceledLastTime) {
        try {
          await this.showFingerPrint();
          await this.doLogin(username, userStored.password, keepSession, false);
        } catch (error) {
          console.error(error);
          if (error.code && error.code === -108) {
            const obj = {
              useFingerPrint: true,
              password: userStored.password,
              canceledLastTime: true,
            };
            localStorage.setItem(username, JSON.stringify(obj));
          } else {
            this.utils.showToast('', `${this.translate.instant('LOGIN.ERROR_USING_BIOMETRIC')}`);
          }
        }
      } else {
        if (userStored.canceledLastTime) {
          await this.doLogin(username, password, keepSession, false);
          const obj = {
            useFingerPrint: true,
            password: userStored.password,
            canceledLastTime: false,
          };
          localStorage.setItem(username, JSON.stringify(obj));
        }
      }
    } else if (form.invalid) {
      this.utils.validateAllFields(form);
      this.showNoteWithCredentials = true;
    } else {
      await this.doLogin(username, password, keepSession, true);
    }
  }

  private async doLogin(username: any, password: any, keepSession: any, saveFingerPrint: boolean) {
    try {
      await this.utils.presentLoading();
      this.indexedDBService.clearIndexedDB();

      const { data } = await this.loginService.doLogin({ username, password });
      AuthenticationService.instance.setAuthentication(data.token, keepSession);
      const { userInformations }: UserTokenData = this.tokenService.deserializeToken(data.token);
      // admin group software
      if (userInformations.profileId === PROFILEIDS.adminGroupSoftware) {
        AuthenticationService.instance.setAuthentication();
        this.router.navigate(['/'], { queryParamsHandling: 'preserve' });
        this.utils.showToast('', `${this.translate.instant('LOGIN.YOU_DO_NOT_HAVE')}`);
      } else {
        this.lastUserLogged = localStorage.setItem('lastUserLogged', JSON.stringify(username));
        if (this.isMobile && saveFingerPrint) {
          this.utils.dismissLoading();
          await this.saveFingerPrint(password, username, userInformations);
        } else {
          this.goToSurvey(userInformations);
        }
      }
    } catch (e) {
      console.error(e);
      let code: string;
      if (e?.error?.code) {
        code = e.error.code;
      }
      if (code === 'authenticate/plan-expired') {
        this.alertController
          .create({
            header: `${this.translate.instant('LOGIN.FREE_EVALUATION')}`,
            message: `${this.translate.instant('LOGIN.THE_TRIAL_PERIOD_IS')}`,
            backdropDismiss: true,
            buttons: [
              {
                text: `${this.translate.instant('LOGIN.DECIDE_LATER')}`,
              },
            ],
          })
          .then(a => a.present());
        return;
      }

      if (this.networkService.getCurrentNetworkStatus() === ConnectionStatus.Online) {
        this.showNoteWithoutCredentials = true;
      } else {
        this.utils.showToast('', `${this.translate.instant('LOGIN.PLEASE_CHECK_YOUR')}`);
      }
    } finally {
      this.utils.dismissLoading();
    }
  }

  private async saveFingerPrint(password: string, username: string, userInformations: UserInformations) {
    await this.faio
      .isAvailable()
      .then(async () => {
        await this.utils.showAlert(
          `${this.translate.instant('LOGIN.DO_YOU_WANT_TO_USE')}`,
          `${this.translate.instant('LOGIN.ENABLE_YOUR_DIGITAL_ACCESS')}`,
          async () => {
            const result = await this.showFingerPrint();
            if (result) {
              const obj = {
                useFingerPrint: true,
                password,
              };
              localStorage.setItem(username, JSON.stringify(obj));
            }
            this.goToSurvey(userInformations);
          },
          async () => {
            this.goToSurvey(userInformations);
          }
        );
      })
      .catch(() => {
        this.goToSurvey(userInformations);
      });
  }

  private showFingerPrint() {
    const options: FingerprintOptions = {};
    options.cancelButtonTitle = `${this.translate.instant('LOGIN.CANCEL')}`;
    options.description = `${this.translate.instant('LOGIN.DIGITAL_ACCESS')}`;
    options.disableBackup = true;
    options.fallbackButtonTitle = `${this.translate.instant('LOGIN.RETURN')}`;
    options.subtitle = `${this.translate.instant('LOGIN.AUTHENTICATION')}`;
    options.title = `${this.translate.instant('LOGIN.GROUP_SURVEY')}`;
    return this.faio.show(options);
  }

  goToRecoveryPassword() {
    this.router.navigate(['login', 'recuperar-senha'], { queryParamsHandling: 'preserve' });
  }

  async usernameOnBlur() {
    const userStored = JSON.parse(localStorage.getItem(this.username.value));
    if (userStored) {
      if (userStored.useFingerPrint) {
        try {
          await this.showFingerPrint();
          await this.doLogin(this.username.value, userStored.password, this.keepSession.value, false);
        } catch (error) {
          console.error(error);
          if (error.code && error.code === -108) {
            const obj = {
              useFingerPrint: true,
              password: userStored.password,
              canceledLastTime: true,
            };
            localStorage.setItem(this.username.value, JSON.stringify(obj));
          } else {
            this.utils.showToast('', `${this.translate.instant('LOGIN.ERROR_USING_BIOMETRIC')}`);
          }
        }
      }
    }
  }

  goToNewUser() {
    this.router.navigate(['novo-usuario'], { queryParamsHandling: 'preserve' });
  }

  async goToSurvey(userInformations: UserInformations) {
    this.initPushNotification(userInformations.userId);
    await this.router.navigate(['vistorias'], { queryParamsHandling: 'preserve' });
    this.loginForm.controls.username.reset();
    this.loginForm.controls.password.reset();
  }

  async showNoticeAfterLogin({ userId, userName }: UserInformations) {
    let arrayIdsShown: number[] = await JSON.parse(localStorage.getItem(KEY_ARRAY_ID_SHOWN));
    if (!arrayIdsShown) {
      arrayIdsShown = [];
    }
    if (!arrayIdsShown.includes(userId)) {
      const modal = await this.popoverController.create({
        component: NoticeAfterLoginModalComponent,
        componentProps: {
          name: userName,
        },
        cssClass: 'notice-after-login-modal',
      });
      await modal.present();
      arrayIdsShown.push(userId);
      localStorage.setItem(KEY_ARRAY_ID_SHOWN, JSON.stringify(arrayIdsShown));
    }
  }

  initPushNotification(userId: number) {
    this.notificationOpened$.subscribe((opened: boolean) => {
      if (opened) {
        this.store.dispatch(new SetNotificationsOpened(false));
        const payload = this.store.selectSnapshot(NotificationState.getNotificationPayoad);
        this.performSurveyService.performSurvey(payload.additionalData.surveyId);
      }
    });
  }

  async hireNow(token: string = this.tokenWhenUserHasExperiredPlan, plan: Plan) {
    try {
      const { planId } = plan;
      await this.utils.presentLoading();
      await this.leadService.sendEmailV2(token, planId);
      this.utils.showToast('', `${this.translate.instant('LOGIN.WE_SEND_YOUR_REQUEST')}`);
    } catch (error) {
      console.error(error);
      if (error?.error?.code) {
        this.utils.showToast('', error.error.message);
      } else {
        this.utils.showToast('', `${this.translate.instant('LOGIN.WE_WERE_UNABLE_TO_SEND')}`);
      }
    } finally {
      this.utils.dismissLoading();
    }
  }
}
